import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Provider, CreateProvider } from '@magnetar/admin-api-interfaces'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { providerService } from '@/services/provider_service'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { Uploader } from '@/components/Uploader'
import { Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import AdminOnly from '@/components/AdminOnly'

type ProviderFormValues = {
  provider_code: string
  provider_name: string
  manager_name: string
  phone: string
  email?: string | null
  address?: string | null
  description?: string | null
  is_active: boolean
  logo_file_uuid?: string | null
}

interface ProviderInfoProps {
  provider?: Provider
  isLoading?: boolean
  mode: 'create' | 'edit'
}

export default function ProviderInfo({ provider, isLoading, mode }: ProviderInfoProps) {
  const { token } = useAuth()
  const navigate = useNavigate()
  const [logoFileUuid, setLogoFileUuid] = useState<string | null>(provider?.logo_file_uuid || null)
  const [showDeleteLogoDialog, setShowDeleteLogoDialog] = useState(false)
  const [showDeleteProviderDialog, setShowDeleteProviderDialog] = useState(false)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')

  const form = useForm<ProviderFormValues>({
    defaultValues: {
      provider_code: provider?.provider_code ?? '',
      provider_name: provider?.provider_name ?? '',
      manager_name: provider?.manager_name ?? '',
      phone: provider?.phone ?? '',
      email: provider?.email ?? null,
      address: provider?.address ?? null,
      description: provider?.description ?? null,
      is_active: provider?.is_active ?? true,
      logo_file_uuid: provider?.logo_file_uuid || null,
    },
  })

  useEffect(() => {
    if (provider && mode === 'edit') {
      form.reset({
        provider_code: provider.provider_code,
        provider_name: provider.provider_name,
        manager_name: provider.manager_name,
        phone: provider.phone,
        email: provider.email ?? null,
        address: provider.address ?? null,
        description: provider.description ?? null,
        is_active: provider.is_active,
        logo_file_uuid: provider.logo_file_uuid,
      })
      setLogoFileUuid(provider.logo_file_uuid)
    }
  }, [provider, form, mode])

  const onSubmit = async (data: ProviderFormValues) => {
    if (!token) return

    try {
      const processedData = {
        ...data,
        logo_file_uuid: logoFileUuid,
        email: data.email && data.email.trim() !== '' ? data.email : null,
        address: data.address && data.address.trim() !== '' ? data.address : null,
        description: data.description && data.description.trim() !== '' ? data.description : null,
      }

      let result

      if (mode === 'create') {
        const createData: CreateProvider = {
          provider_code: processedData.provider_code,
          provider_name: processedData.provider_name,
          manager_name: processedData.manager_name,
          phone: processedData.phone,
          email: processedData.email,
          address: processedData.address,
          description: processedData.description,
          is_active: processedData.is_active,
          logo_file_uuid: processedData.logo_file_uuid,
        }

        result = await providerService.createProvider(createData, token)
      } else {
        result = await providerService.updateProvider(provider!.provider_id, processedData, token)
      }

      if (result.success) {
        toast.success(`組織 ${data.provider_name} 已${mode === 'create' ? '建立' : '更新'}`)
        if (mode === 'create') {
          navigate('/admin/providers')
        }
      } else {
        toast.error(
          (result as { error?: { message?: string } }).error?.message ||
            `組織${mode === 'create' ? '建立' : '更新'}失敗`,
        )
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '組織操作失敗')
    }
  }

  const handleDeleteProvider = async () => {
    if (!token || !provider) return

    setShowDeleteProviderDialog(false)
    setDeleteConfirmInput('')

    try {
      const result = await providerService.deleteProvider(provider.provider_id, token)

      if (result.success) {
        toast.success(`組織 ${provider.provider_name} 已刪除`)
        navigate('/admin/providers')
      } else {
        toast.error((result as { error?: { message?: string } }).error?.message || '組織刪除失敗')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '組織刪除失敗')
    }
  }

  if (mode === 'edit' && isLoading) {
    return <div>載入中...</div>
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{mode === 'create' ? '新增組織' : '編輯組織'}</CardTitle>
          <CardDescription>
            {mode === 'create' ? '建立新的組織資訊' : '編輯現有組織資訊'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <div className="space-y-8">
              <FormField
                control={form.control}
                name="provider_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>組織代碼</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="請輸入組織代碼"
                        disabled={mode === 'edit'}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>唯一識別碼，建立後無法修改</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="provider_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>組織名稱</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入組織名稱" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormItem>
                <FormLabel>組織 Logo</FormLabel>
                <FormControl>
                  <div className="mt-2">
                    {logoFileUuid ? (
                      <div className="relative group mb-4 inline-block">
                        <img
                          src={`${import.meta.env.VITE_API_URL}/api/files/download/${logoFileUuid}`}
                          alt="組織 Logo"
                          className="h-32 object-cover rounded-md"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          type="button"
                          className="absolute right-2 top-2 hidden group-hover:flex"
                          onClick={() => setShowDeleteLogoDialog(true)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Uploader
                        folder="provider_logos"
                        allowedFileTypes={['.jpg', '.jpeg', '.png', '.webp']}
                        onUploadSuccess={({ uuid }) => {
                          setLogoFileUuid(uuid)
                          form.setValue('logo_file_uuid', uuid)
                        }}
                      />
                    )}
                  </div>
                </FormControl>
                <FormDescription>
                  上傳組織 Logo 圖片（支援 jpg、jpeg、png、webp 格式）
                </FormDescription>
                <FormMessage />
              </FormItem>

              <FormField
                control={form.control}
                name="manager_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>負責人姓名</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入負責人姓名" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電話</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入電話" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email（選填）</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="請輸入 Email"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>地址（選填）</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="請輸入地址"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>描述（選填）</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="請輸入描述"
                        className="resize-none"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <AdminOnly>
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>啟用狀態</FormLabel>
                        <FormDescription>設定組織是否啟用</FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </AdminOnly>

              <div className="flex justify-between w-full">
                <Button type="button" onClick={form.handleSubmit(onSubmit)}>
                  {mode === 'create' ? '建立組織' : '更新組織'}
                </Button>

                {mode === 'edit' && (
                  <AdminOnly>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setShowDeleteProviderDialog(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      刪除組織
                    </Button>
                  </AdminOnly>
                )}
              </div>
            </div>
          </Form>
        </CardContent>
      </Card>

      {/* 刪除 Logo 確認對話框 */}
      <AlertDialog open={showDeleteLogoDialog} onOpenChange={setShowDeleteLogoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除組織 Logo 嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原，刪除後需要重新上傳 Logo。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setLogoFileUuid(null)
                form.setValue('logo_file_uuid', null)
                setShowDeleteLogoDialog(false)
              }}
            >
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 刪除組織確認對話框 */}
      <AlertDialog open={showDeleteProviderDialog} onOpenChange={setShowDeleteProviderDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除組織嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原，刪除後組織的所有資料將被移除。請輸入組織名稱「
              {provider?.provider_name}」以確認刪除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder="請輸入組織名稱以確認刪除"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmInput('')}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProvider}
              disabled={deleteConfirmInput !== provider?.provider_name}
              className={
                deleteConfirmInput !== provider?.provider_name ? 'opacity-50 cursor-not-allowed' : ''
              }
            >
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
